import React, { useState, useEffect } from 'react';
import { Download, Upload, AlertTriangle, CheckCircle, RefreshCw, Clock, Mail, Send } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BackupTab() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [restoreMode, setRestoreMode] = useState('merge');
  const [restoreResult, setRestoreResult] = useState(null);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [autoBackupStatus, setAutoBackupStatus] = useState(null);
  const [triggeringBackup, setTriggeringBackup] = useState(false);
  const [triggerResult, setTriggerResult] = useState(null);

  useEffect(() => { fetchAutoBackupStatus(); }, []);

  const fetchAutoBackupStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/blog/admin/backup/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAutoBackupStatus(res.data);
    } catch {}
  };

  const handleTriggerAutoBackup = async () => {
    setTriggeringBackup(true);
    setTriggerResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/api/blog/admin/backup/trigger`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTriggerResult(res.data);
      fetchAutoBackupStatus();
    } catch (err) {
      setTriggerResult({ status: 'error', message: err.response?.data?.detail || err.message });
    } finally {
      setTriggeringBackup(false);
    }
  };

  const handleBackup = async () => {
    setLoading(true);
    setStatus(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/blog/admin/backup/articles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data;
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const date = new Date().toISOString().split('T')[0];
      a.href = url;
      a.download = `pakdeheru-articles-backup-${date}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setStatus({ type: 'success', message: `Backup berhasil! ${data.total_articles} artikel di-download.` });
    } catch (err) {
      setStatus({ type: 'error', message: 'Gagal membuat backup: ' + (err.response?.data?.detail || err.message) });
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (restoreMode === 'replace' && !confirmReplace) {
      setConfirmReplace(true);
      e.target.value = '';
      return;
    }

    setLoading(true);
    setRestoreResult(null);
    setConfirmReplace(false);

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const articles = data.articles || data;
      if (!Array.isArray(articles) || articles.length === 0) {
        setRestoreResult({ type: 'error', message: 'File tidak berisi artikel yang valid.' });
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/api/blog/admin/backup/restore`, {
        articles,
        mode: restoreMode,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const r = res.data;
      setRestoreResult({
        type: 'success',
        message: `Restore berhasil! ${r.restored} artikel ditambahkan, ${r.skipped} dilewati (sudah ada).`,
      });
    } catch (err) {
      setRestoreResult({ type: 'error', message: 'Gagal restore: ' + (err.response?.data?.detail || err.message) });
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  return (
    <div style={{ padding: '24px 0' }}>
      <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#EDEDED', marginBottom: '8px' }}>
        Backup & Restore Artikel
      </h2>
      <p style={{ color: '#9CA3AF', fontSize: '0.875rem', marginBottom: '24px' }}>
        Download semua artikel sebagai file JSON, atau pulihkan dari backup sebelumnya.
      </p>

      {/* Auto-Backup Status */}
      <div data-testid="auto-backup-section" style={{
        background: '#1A1A2E', border: '1px solid #2A2A3E', borderRadius: '12px',
        padding: '20px', marginBottom: '20px',
      }}>
        <h3 style={{ color: '#d9fb06', fontSize: '1rem', fontWeight: 600, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Clock size={18} /> Auto-Backup Mingguan
        </h3>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '0.8rem', color: '#9CA3AF', marginBottom: '12px' }}>
          <span>Jadwal: <strong style={{ color: '#EDEDED' }}>Senin 03:00 WIB</strong></span>
          <span>Penerima: <strong style={{ color: '#EDEDED' }}>admin@pakdeheru.com</strong></span>
          <span>Status: <strong style={{ color: '#4ADE80' }}>Aktif</strong></span>
        </div>
        {autoBackupStatus?.last_backup && (
          <div style={{ fontSize: '0.8rem', color: '#71717A', marginBottom: '12px' }}>
            Backup terakhir: <strong style={{ color: '#A1A1AA' }}>
              {new Date(autoBackupStatus.last_backup.created_at).toLocaleString('id-ID')}
            </strong>
            {' '}({autoBackupStatus.last_backup.total_articles} artikel)
            {' | '}Total backup terkirim: <strong style={{ color: '#d9fb06' }}>{autoBackupStatus.total_backups_sent}</strong>
          </div>
        )}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            data-testid="trigger-auto-backup-btn"
            onClick={handleTriggerAutoBackup}
            disabled={triggeringBackup}
            style={{
              background: 'rgba(255,255,255,0.08)', color: '#EDEDED', border: 'none', borderRadius: '8px',
              padding: '8px 16px', fontSize: '0.8rem', cursor: triggeringBackup ? 'wait' : 'pointer',
              display: 'flex', alignItems: 'center', gap: '6px', opacity: triggeringBackup ? 0.6 : 1,
            }}
          >
            {triggeringBackup ? <RefreshCw size={14} className="spin" /> : <Send size={14} />}
            {triggeringBackup ? 'Mengirim...' : 'Kirim Backup Sekarang'}
          </button>
        </div>
        {triggerResult && (
          <div data-testid="trigger-backup-result" style={{
            marginTop: '10px', padding: '8px 12px', borderRadius: '8px',
            background: triggerResult.status === 'success' ? '#0D2818' : '#2D1215',
            color: triggerResult.status === 'success' ? '#4ADE80' : '#F87171',
            fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            {triggerResult.status === 'success' ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
            {triggerResult.status === 'success'
              ? `Backup berhasil dikirim ke ${triggerResult.sent_to} (${triggerResult.articles} artikel)`
              : `Gagal: ${triggerResult.message}`}
          </div>
        )}
      </div>

      {/* Backup Section */}
      <div data-testid="backup-section" style={{
        background: '#1A1A2E', border: '1px solid #2A2A3E', borderRadius: '12px',
        padding: '20px', marginBottom: '20px',
      }}>
        <h3 style={{ color: '#d9fb06', fontSize: '1rem', fontWeight: 600, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Download size={18} /> Download Backup
        </h3>
        <p style={{ color: '#9CA3AF', fontSize: '0.8rem', marginBottom: '16px' }}>
          Seluruh artikel (draft, published, archived) akan di-export ke file JSON.
        </p>
        <button
          data-testid="backup-download-btn"
          onClick={handleBackup}
          disabled={loading}
          style={{
            background: '#d9fb06', color: '#0A0A0A', border: 'none', borderRadius: '8px',
            padding: '10px 20px', fontWeight: 600, cursor: loading ? 'wait' : 'pointer',
            display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem',
            opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? <RefreshCw size={16} className="spin" /> : <Download size={16} />}
          {loading ? 'Downloading...' : 'Download Backup'}
        </button>
        {status && (
          <div data-testid="backup-status" style={{
            marginTop: '12px', padding: '10px 14px', borderRadius: '8px',
            background: status.type === 'success' ? '#0D2818' : '#2D1215',
            color: status.type === 'success' ? '#4ADE80' : '#F87171',
            fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '8px',
          }}>
            {status.type === 'success' ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
            {status.message}
          </div>
        )}
      </div>

      {/* Restore Section */}
      <div data-testid="restore-section" style={{
        background: '#1A1A2E', border: '1px solid #2A2A3E', borderRadius: '12px',
        padding: '20px',
      }}>
        <h3 style={{ color: '#d9fb06', fontSize: '1rem', fontWeight: 600, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Upload size={18} /> Restore dari Backup
        </h3>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ color: '#9CA3AF', fontSize: '0.8rem', display: 'block', marginBottom: '8px' }}>Mode Restore:</label>
          <div style={{ display: 'flex', gap: '12px' }}>
            <label data-testid="restore-mode-merge" style={{
              display: 'flex', alignItems: 'center', gap: '6px', color: '#EDEDED',
              cursor: 'pointer', fontSize: '0.85rem',
            }}>
              <input
                type="radio" name="mode" value="merge"
                checked={restoreMode === 'merge'}
                onChange={() => { setRestoreMode('merge'); setConfirmReplace(false); }}
              />
              Merge (tambahkan yang belum ada)
            </label>
            <label data-testid="restore-mode-replace" style={{
              display: 'flex', alignItems: 'center', gap: '6px', color: '#F87171',
              cursor: 'pointer', fontSize: '0.85rem',
            }}>
              <input
                type="radio" name="mode" value="replace"
                checked={restoreMode === 'replace'}
                onChange={() => setRestoreMode('replace')}
              />
              Replace (hapus semua, ganti dari backup)
            </label>
          </div>
        </div>

        {confirmReplace && (
          <div data-testid="replace-confirm" style={{
            background: '#2D1215', border: '1px solid #F87171', borderRadius: '8px',
            padding: '14px', marginBottom: '16px',
          }}>
            <p style={{ color: '#F87171', fontSize: '0.85rem', fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <AlertTriangle size={16} /> Peringatan!
            </p>
            <p style={{ color: '#FCA5A5', fontSize: '0.8rem', marginBottom: '12px' }}>
              Mode Replace akan menghapus SEMUA artikel yang ada dan menggantinya dengan isi backup. Tindakan ini tidak bisa dibatalkan.
            </p>
            <label style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: '#F87171', color: '#0A0A0A', borderRadius: '8px',
              padding: '10px 20px', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem',
            }}>
              <Upload size={16} /> Ya, Lanjutkan Replace
              <input
                type="file" accept=".json" onChange={handleRestore}
                style={{ display: 'none' }}
              />
            </label>
            <button
              onClick={() => setConfirmReplace(false)}
              style={{
                background: 'transparent', color: '#9CA3AF', border: '1px solid #4A4A5E',
                borderRadius: '8px', padding: '10px 20px', marginLeft: '8px',
                cursor: 'pointer', fontSize: '0.85rem',
              }}
            >
              Batal
            </button>
          </div>
        )}

        {!confirmReplace && (
          <label style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            background: restoreMode === 'replace' ? '#7F1D1D' : '#1E3A5F',
            color: '#EDEDED', borderRadius: '8px', padding: '10px 20px',
            fontWeight: 600, cursor: loading ? 'wait' : 'pointer', fontSize: '0.85rem',
            opacity: loading ? 0.6 : 1,
          }}>
            {loading ? <RefreshCw size={16} className="spin" /> : <Upload size={16} />}
            {loading ? 'Restoring...' : 'Pilih File Backup (.json)'}
            <input
              data-testid="restore-file-input"
              type="file" accept=".json" onChange={handleRestore}
              disabled={loading}
              style={{ display: 'none' }}
            />
          </label>
        )}

        {restoreResult && (
          <div data-testid="restore-status" style={{
            marginTop: '12px', padding: '10px 14px', borderRadius: '8px',
            background: restoreResult.type === 'success' ? '#0D2818' : '#2D1215',
            color: restoreResult.type === 'success' ? '#4ADE80' : '#F87171',
            fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '8px',
          }}>
            {restoreResult.type === 'success' ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
            {restoreResult.message}
          </div>
        )}
      </div>
    </div>
  );
}
